import { useMemo, useState } from "react";
import type {
  CoachingClip,
  CoachingNote,
  CoachingPlaylist,
  FeedbackBundle,
  MatchSummary,
} from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { Button } from "@/components/ui/Button";
import {
  buildFeedbackReviewItems,
  type FeedbackReviewItem,
} from "./feedbackReviewItems";
import { shortDateLabel, summarySections } from "./feedbackHelpers";
import { buildPlayerResponseIndex } from "./responseSignals";

/**
 * Feedback History — PR 5 of the Feedback Player UX redesign
 * (`docs/FEEDBACK_PLAYER_UX_PLAN.md`, Phase 7).
 *
 * Grouped archive of everything the Review tab is intentionally
 * NOT pushing as the player's next action — reviewed notes,
 * reviewed sessions, older clips, and older match summaries.
 * Replaces the PR 1 thin wrapper.
 *
 * Approach: reuse the PR 2 normalized review item list. The "what
 * belongs here" rule is intentionally simple — anything not in the
 * Review tab's primary attention surface:
 *   1. Reviewed notes (`status === "reviewed"`, kind `note`).
 *   2. Reviewed sessions (`status === "reviewed"`, kind `playlist`).
 *   3. Older clips — clips beyond a 30-day recency cutoff.
 *   4. Older match summaries — summaries beyond the same cutoff.
 *
 * The 30-day cutoff is a pure UI threshold (no backend support
 * needed); the bundle is already filtered to the viewer's team /
 * scope server-side. Recently shared clips + summaries stay on the
 * Review tab so the player can act on them; older ones move here to
 * keep Review action-focused.
 *
 * "Older clips" includes ALL clips that miss the cutoff, regardless
 * of any review-tracking flag — clips currently have no per-item
 * reviewed contract (PR 6 will revisit), so they sit between "new
 * to review" and "history" based on time.
 *
 * Optional filter pill row (All / Reviewed / Older) lets the
 * player narrow the archive when it grows.
 */

export type FeedbackHistoryTabProps = {
  bundle: FeedbackBundle | undefined;
  reviewedNoteIds: Set<number>;
  reviewedPlaylistIds: Set<number>;
};

/** Cutoff in days for "older" clips + summaries. UI-only — the
 * server already scopes the bundle to the viewer's team. */
const OLDER_THAN_DAYS = 30;

type HistoryFilter = "all" | "reviewed" | "older";

export function FeedbackHistoryTab({
  bundle,
  reviewedNoteIds,
  reviewedPlaylistIds,
}: FeedbackHistoryTabProps) {
  // The full normalized list — same source the Review feed uses.
  const all = useMemo(
    () =>
      buildFeedbackReviewItems({
        bundle,
        reviewedNoteIds,
        reviewedPlaylistIds,
      }),
    [bundle, reviewedNoteIds, reviewedPlaylistIds],
  );
  // PR 6 — player response signals (Got it / Need help / I tried this).
  const responseIndex = useMemo(
    () => buildPlayerResponseIndex(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  // Apply the History filter: reviewed items keep, untracked clips +
  // summaries kept only if they're past the recency cutoff.
  const cutoffMs = useMemo(
    () => Date.now() - OLDER_THAN_DAYS * 24 * 60 * 60 * 1000,
    [],
  );
  const historyItems = useMemo(() => {
    return all.filter((it) => {
      if (it.status === "reviewed") return true;
      if (it.kind === "clip" || it.kind === "summary") {
        const ts = it.dateIso ? Date.parse(it.dateIso) : NaN;
        return Number.isFinite(ts) && ts < cutoffMs;
      }
      return false;
    });
  }, [all, cutoffMs]);

  const reviewedNotes = historyItems.filter(
    (i) => i.kind === "note" && i.status === "reviewed",
  );
  const reviewedSessions = historyItems.filter(
    (i) => i.kind === "playlist" && i.status === "reviewed",
  );
  const olderClips = historyItems.filter((i) => i.kind === "clip");
  const olderSummaries = historyItems.filter((i) => i.kind === "summary");

  const [filter, setFilter] = useState<HistoryFilter>("all");
  const matchesFilter = (item: FeedbackReviewItem) => {
    if (filter === "all") return true;
    if (filter === "reviewed") return item.status === "reviewed";
    if (filter === "older") return item.status !== "reviewed";
    return true;
  };

  const filteredCount = historyItems.filter(matchesFilter).length;

  // Modal wiring — reuse the M5 player so a card opens in place.
  const [openNote, setOpenNote] = useState<CoachingNote | null>(null);
  const [openClip, setOpenClip] = useState<CoachingClip | null>(null);
  const [openPlaylist, setOpenPlaylist] = useState<CoachingPlaylist | null>(
    null,
  );
  const [expandedSummaryId, setExpandedSummaryId] = useState<number | null>(
    null,
  );

  function openItem(item: FeedbackReviewItem) {
    if (item.source.kind === "note") setOpenNote(item.source.note);
    else if (item.source.kind === "clip") setOpenClip(item.source.clip);
    else if (item.source.kind === "playlist")
      setOpenPlaylist(item.source.playlist);
    else if (item.source.kind === "summary") {
      const id = item.source.summary.id;
      setExpandedSummaryId((cur) => (cur === id ? null : id));
    }
  }

  if (historyItems.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm px-3 py-6"
        data-testid="feedback-history-empty"
      >
        Nothing in your history yet. Items show up here once you've marked
        them reviewed, or when older clips and match recaps move out of the
        Review feed.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="feedback-history">
      <HistoryFilterStrip
        active={filter}
        onSelect={setFilter}
        counts={{
          all: historyItems.length,
          reviewed: reviewedNotes.length + reviewedSessions.length,
          older: olderClips.length + olderSummaries.length,
        }}
      />

      {filteredCount === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="feedback-history-filter-empty"
        >
          Nothing in this filter. Try another chip above.
        </p>
      ) : (
        <div className="flex flex-col gap-8">
          {reviewedSessions.length > 0 && filter !== "older" ? (
            <HistorySection
              heading="Sessions reviewed"
              testid="feedback-history-sessions"
              items={reviewedSessions}
              expandedSummaryId={expandedSummaryId}
              onOpen={openItem}
            />
          ) : null}
          {reviewedNotes.length > 0 && filter !== "older" ? (
            <HistorySection
              heading="Notes reviewed"
              testid="feedback-history-notes"
              items={reviewedNotes}
              expandedSummaryId={expandedSummaryId}
              onOpen={openItem}
            />
          ) : null}
          {olderClips.length > 0 && filter !== "reviewed" ? (
            <HistorySection
              heading="Older clips"
              testid="feedback-history-clips"
              items={olderClips}
              expandedSummaryId={expandedSummaryId}
              onOpen={openItem}
            />
          ) : null}
          {olderSummaries.length > 0 && filter !== "reviewed" ? (
            <HistorySection
              heading="Older match recaps"
              testid="feedback-history-summaries"
              items={olderSummaries}
              expandedSummaryId={expandedSummaryId}
              onOpen={openItem}
            />
          ) : null}
        </div>
      )}

      {openNote ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: openNote }}
          match={{ id: openNote.match_id ?? "", team_id: openNote.team_id }}
          onClose={() => setOpenNote(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
          playerResponses={responseIndex}
        />
      ) : null}
      {openClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: openClip }}
          match={{ id: openClip.match_id ?? "", team_id: openClip.team_id }}
          onClose={() => setOpenClip(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
        />
      ) : null}
      {openPlaylist ? (
        <FeedbackPlayerModal
          target={{ kind: "playlist", playlist: openPlaylist }}
          match={{
            id: openPlaylist.items?.[0]?.match_id ?? "",
            team_id:
              openPlaylist.items?.[0]?.team_id ?? openPlaylist.team_id,
          }}
          onClose={() => setOpenPlaylist(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
          playerResponses={responseIndex}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter strip
// ---------------------------------------------------------------------------

function HistoryFilterStrip({
  active,
  counts,
  onSelect,
}: {
  active: HistoryFilter;
  counts: Record<HistoryFilter, number>;
  onSelect: (f: HistoryFilter) => void;
}) {
  const FILTERS: ReadonlyArray<{ id: HistoryFilter; label: string }> = [
    { id: "all", label: "All" },
    { id: "reviewed", label: "Reviewed" },
    { id: "older", label: "Older" },
  ];
  return (
    <div
      role="tablist"
      aria-label="Filter history"
      data-testid="feedback-history-filters"
      className="flex gap-1.5 overflow-x-auto rounded-md border border-rule bg-bg-card px-2 py-1.5"
    >
      {FILTERS.map((f) => {
        const isActive = active === f.id;
        const count = counts[f.id];
        return (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-feedback-history-filter={f.id}
            data-active={isActive}
            onClick={() => onSelect(f.id)}
            className={[
              "shrink-0 rounded-full px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors",
              isActive
                ? "bg-accent text-accent-ink"
                : "bg-surface text-ink-muted hover:text-ink hover:bg-bg-card border border-rule",
            ].join(" ")}
          >
            {f.label}
            {count > 0 ? (
              <span
                className={[
                  "ml-1.5 text-[0.6rem] font-mono",
                  isActive ? "text-accent-ink/70" : "text-ink-muted",
                ].join(" ")}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section + history card
// ---------------------------------------------------------------------------

function HistorySection({
  heading,
  testid,
  items,
  expandedSummaryId,
  onOpen,
}: {
  heading: string;
  testid: string;
  items: FeedbackReviewItem[];
  expandedSummaryId: number | null;
  onOpen: (item: FeedbackReviewItem) => void;
}) {
  return (
    <section className="flex flex-col gap-3" data-testid={testid}>
      <h3 className="font-display text-base text-ink">{heading}</h3>
      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <HistoryRow
            key={item.id}
            item={item}
            expanded={
              item.source.kind === "summary" &&
              expandedSummaryId === item.source.summary.id
            }
            onOpen={() => onOpen(item)}
          />
        ))}
      </ul>
    </section>
  );
}

function HistoryRow({
  item,
  expanded,
  onOpen,
}: {
  item: FeedbackReviewItem;
  expanded: boolean;
  onOpen: () => void;
}) {
  const dateLabel = item.dateIso ? shortDateLabel(item.dateIso) : "";
  const metaParts: string[] = [];
  if (item.matchLabel) metaParts.push(item.matchLabel);
  if (dateLabel && !item.matchLabel?.includes(dateLabel)) metaParts.push(dateLabel);

  const actionLabel =
    item.kind === "playlist"
      ? "Replay session"
      : item.kind === "summary"
        ? expanded
          ? "Hide recap"
          : "Read recap"
        : "Review again";

  return (
    <li>
      <article
        data-testid="feedback-history-card"
        data-history-item-kind={item.kind}
        data-history-status={item.status}
        className="flex flex-col gap-2 rounded-md border border-rule bg-bg-card p-3 sm:flex-row sm:items-start"
      >
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <span
            className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted bg-surface border border-rule rounded px-1.5 py-0.5"
            data-testid="feedback-history-card-type"
          >
            {item.typeLabel}
          </span>
          {item.status === "reviewed" ? (
            <span
              className="text-[0.6rem] font-display uppercase tracking-wider text-good"
              data-testid="feedback-history-card-reviewed-chip"
            >
              ✓ Reviewed
            </span>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="font-display text-sm text-ink truncate"
            data-testid="feedback-history-card-title"
          >
            {item.title}
          </p>
          {metaParts.length > 0 ? (
            <p
              className="text-ink-muted text-[0.7rem] font-mono mt-0.5 truncate"
              data-testid="feedback-history-card-meta"
            >
              {metaParts.join(" · ")}
            </p>
          ) : null}
          {expanded && item.source.kind === "summary" ? (
            <SummaryExpanded summary={item.source.summary} />
          ) : null}
        </div>
        <div className="shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpen}
            data-testid="feedback-history-card-action"
          >
            {actionLabel}
          </Button>
        </div>
      </article>
    </li>
  );
}

function SummaryExpanded({ summary }: { summary: MatchSummary }) {
  const sections = summarySections(summary);
  if (sections.length === 0) return null;
  return (
    <div
      className="mt-3 flex flex-col gap-2 rounded-md border border-rule bg-surface p-3"
      data-testid="feedback-history-card-summary-expanded"
    >
      {sections.map(([label, value]) => (
        <div key={label}>
          <p className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
            {label}
          </p>
          <p className="text-sm text-ink mt-1">{value}</p>
        </div>
      ))}
    </div>
  );
}
